import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Briefcase, CircleDollarSign, CalendarDays, Trophy, Plus, Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { StatusPill, SlotStatusPill, money } from "./OverviewTab";

const STATUSES = ["Pending", "In Review", "Proposal Sent", "In Development", "Completed"];
const SLOT_STATUSES = ["Booked", "Onboarded", "In Progress", "Live"];
const ROLE_OPTIONS = ["client", "employee", "admin"];

const inputCls =
  "w-full bg-obsidian border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt/60 transition-colors duration-300";

export function RoleBadge({ role }) {
  const cls =
    role === "admin"
      ? "text-volt border-volt/30 bg-volt/10"
      : role === "employee"
        ? "text-sky-400 border-sky-400/30 bg-sky-400/10"
        : "text-slate-400 border-white/15 bg-white/5";
  return <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${cls}`}>{role}</span>;
}

export default function AdminTab() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [slots, setSlots] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [notes, setNotes] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/admin/slots/leaderboard").then((r) => setLeaderboard(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post("/admin/users", createForm);
      setCreated({ ...createForm, zid: res.data.zid });
      setCreateForm({ name: "", email: "", password: "", role: "client" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const copyCreds = async () => {
    try {
      await navigator.clipboard.writeText(`Zork ID: ${created.zid}\nEmail: ${created.email}\nPassword: ${created.password}`);
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const updateRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.zid} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateSlotStatus = async (id, status) => {
    try {
      await api.patch(`/admin/slots/${id}/status`, { status });
      toast.success(`Slot marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateEnquiry = async (id, patch) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, patch);
      toast.success("Enquiry updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statCards = stats
    ? [
        { icon: Users, label: "Total Users", value: stats.total_users, testid: "admin-stat-users" },
        { icon: Briefcase, label: "Total Briefs", value: stats.total_enquiries, testid: "admin-stat-enquiries" },
        { icon: CalendarDays, label: "Shop Slots", value: stats.total_slots, testid: "admin-stat-slots" },
        { icon: CircleDollarSign, label: "Global Net", value: money(stats.net), testid: "admin-stat-net" },
      ]
    : [];

  return (
    <div data-testid="admin-tab" className="space-y-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
        {statCards.map((c) => (
          <div key={c.label} className="bg-obsidian p-8">
            <c.icon size={20} strokeWidth={1.5} className="text-volt" />
            <p data-testid={c.testid} className="mt-8 font-display font-extrabold tracking-tight text-3xl">{c.value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      {stats && (
        <div className="flex flex-wrap gap-3" data-testid="admin-status-breakdown">
          {Object.entries(stats.enquiries_by_status).map(([s, n]) => (
            <span key={s} className="font-mono text-[10px] uppercase tracking-[0.2em] border border-white/10 px-4 py-2 text-slate-400">
              {s}: <span className="text-volt">{n}</span>
            </span>
          ))}
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-sky-400/30 px-4 py-2 text-slate-400">
            Employees: <span className="text-sky-400">{stats.total_employees}</span>
          </span>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-semibold text-xl tracking-tight">All Users</h3>
          <button
            data-testid="admin-create-user-btn"
            onClick={() => { setCreated(null); setCreateOpen(true); }}
            className="flex items-center gap-2 bg-volt text-obsidian font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-3 hover:bg-[#CCEE00] transition-colors duration-300"
          >
            <Plus size={14} /> Create User ID
          </button>
        </div>
        <div className="border border-white/5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-white/5 bg-panel/60">
                {["ID", "Name", "Email", "Role", "Joined", "Change Role"].map((h) => (
                  <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 px-5 py-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-panel/40 transition-colors duration-200">
                  <td className={`px-5 py-4 font-mono text-xs ${u.zid.startsWith("ZE") ? "text-sky-400" : "text-volt"}`}>{u.zid}</td>
                  <td className="px-5 py-4">{u.name}</td>
                  <td className="px-5 py-4 text-slate-400">{u.email}</td>
                  <td className="px-5 py-4"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-4 font-mono text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`role-select-${u.zid}`}
                      value={u.role}
                      onChange={(ev) => updateRole(u, ev.target.value)}
                      className="bg-obsidian border border-white/10 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-200 focus:outline-none focus:border-volt/60"
                    >
                      {ROLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="font-display font-semibold text-xl tracking-tight mb-6">Employee Leaderboard</h3>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-slate-500 border border-dashed border-white/10 p-12 text-center">No bookings yet — the leaderboard lights up as employees book shops.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leaderboard">
            {leaderboard.map((e, i) => (
              <div key={e.user_zid} data-testid={`leaderboard-${e.user_zid}`} className={`border p-6 ${i === 0 ? "border-volt/50 bg-volt/5" : "border-white/5 bg-panel/40"}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${i === 0 ? "text-volt" : "text-slate-500"}`}>
                    {i === 0 ? <span className="inline-flex items-center gap-1.5"><Trophy size={12} /> Top Performer</span> : `Rank ${i + 1}`}
                  </span>
                  <span className="font-mono text-xs text-sky-400">{e.user_zid}</span>
                </div>
                <p className="mt-4 font-display font-semibold text-lg tracking-tight">{e.user_name}</p>
                <div className="mt-4 flex gap-6">
                  <div>
                    <p className="font-display font-extrabold text-2xl text-volt">{e.this_month}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">This month</p>
                  </div>
                  <div>
                    <p className="font-display font-extrabold text-2xl">{e.total}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">All time</p>
                  </div>
                  <div>
                    <p className="font-display font-extrabold text-2xl text-green-400">{e.live}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Live</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="font-display font-semibold text-xl tracking-tight mb-6">Shop Slots</h3>
        {slots.length === 0 ? (
          <p className="text-sm text-slate-500 border border-dashed border-white/10 p-12 text-center">No shop slots booked yet. Employee bookings will appear here.</p>
        ) : (
          <div className="border border-white/5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-slots-table">
              <thead>
                <tr className="border-b border-white/5 bg-panel/60">
                  {["Date", "Shop / Company", "Contact", "GSTIN", "Insta", "Status", "Address", "Booked By"].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.map((s) => (
                  <tr key={s.id} data-testid={`admin-slot-${s.id.slice(0, 8)}`} className="border-b border-white/5 hover:bg-panel/40 transition-colors duration-200">
                    <td className="px-5 py-4"><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-obsidian bg-volt font-bold px-2.5 py-1 whitespace-nowrap">{s.date}</span></td>
                    <td className="px-5 py-4 font-medium">{s.shop_name}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-400">{s.contact}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-400">{s.gstin || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${s.instagram ? "text-volt border-volt/30 bg-volt/10" : "text-slate-500 border-white/10"}`}>
                        {s.instagram ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`slot-status-select-${s.id.slice(0, 8)}`}
                        value={s.status || "Booked"}
                        onChange={(ev) => updateSlotStatus(s.id, ev.target.value)}
                        className="bg-obsidian border border-white/10 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-200 focus:outline-none focus:border-volt/60"
                      >
                        {SLOT_STATUSES.map((st) => <option key={st}>{st}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-slate-400 text-xs max-w-[220px]">{s.address}</td>
                    <td className="px-5 py-4">
                      <span className="font-mono text-xs text-sky-400">{s.user_zid}</span>
                      <span className="block text-xs text-slate-500">{s.user_name}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="font-display font-semibold text-xl tracking-tight mb-6">All Project Briefs</h3>
        {enquiries.length === 0 ? (
          <p className="text-sm text-slate-500 border border-dashed border-white/10 p-12 text-center">No briefs submitted yet.</p>
        ) : (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {enquiries.map((e) => (
              <article key={e.id} className="border border-white/5 bg-panel/40 p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h4 className="font-display font-semibold text-lg tracking-tight">{e.title}</h4>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {e.user_zid} · {e.user_name} · {e.service} · {e.budget}
                    </p>
                  </div>
                  <StatusPill status={e.status} />
                </div>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-3xl">{e.details}</p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <select
                    data-testid={`admin-status-select-${e.id.slice(0, 8)}`}
                    value={e.status}
                    onChange={(ev) => updateEnquiry(e.id, { status: ev.target.value })}
                    className="bg-obsidian border border-white/10 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-200 focus:outline-none focus:border-volt/60"
                  >
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <input
                    data-testid={`admin-note-input-${e.id.slice(0, 8)}`}
                    placeholder="Note to client..."
                    defaultValue={e.admin_note}
                    onChange={(ev) => setNotes({ ...notes, [e.id]: ev.target.value })}
                    className="flex-1 min-w-[200px] bg-obsidian border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-volt/60"
                  />
                  <button
                    data-testid={`admin-note-save-${e.id.slice(0, 8)}`}
                    onClick={() => updateEnquiry(e.id, { admin_note: notes[e.id] ?? e.admin_note })}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] bg-volt text-obsidian font-bold px-4 py-2 hover:bg-[#CCEE00] transition-colors duration-300"
                  >
                    Save Note
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-panel border-white/10 text-slate-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Create User ID</DialogTitle>
          </DialogHeader>
          {created ? (
            <div className="mt-2 space-y-5" data-testid="created-credentials">
              <p className="text-sm text-slate-400">Account provisioned. Share these credentials — they cannot sign up themselves.</p>
              <div className="border border-volt/30 bg-volt/5 p-5 space-y-2 font-mono text-sm">
                <p><span className="text-slate-500 text-[10px] uppercase tracking-[0.25em] block">ID</span><span className="text-volt text-xl tracking-[0.15em]" data-testid="created-zid">{created.zid}</span></p>
                <p><span className="text-slate-500 text-[10px] uppercase tracking-[0.25em] block">Email</span>{created.email}</p>
                <p><span className="text-slate-500 text-[10px] uppercase tracking-[0.25em] block">Password</span>{created.password}</p>
                <p><span className="text-slate-500 text-[10px] uppercase tracking-[0.25em] block">Role</span><span className="uppercase">{created.role}</span></p>
              </div>
              <div className="flex gap-3">
                <button data-testid="copy-credentials-btn" onClick={copyCreds} className="flex items-center gap-2 bg-volt text-obsidian font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-3 hover:bg-[#CCEE00] transition-colors duration-300">
                  {copied ? <Check size={14} /> : <Copy size={14} />} Copy Credentials
                </button>
                <button data-testid="create-another-btn" onClick={() => setCreated(null)} className="border border-white/15 text-slate-300 font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3 hover:border-volt/50 hover:text-volt transition-colors duration-300">
                  Create Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={createUser} data-testid="create-user-form" className="space-y-4 mt-2">
              <input data-testid="create-name-input" required minLength={2} placeholder="Full name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className={inputCls} />
              <input data-testid="create-email-input" required type="email" placeholder="Email (this is their login ID)" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className={inputCls} />
              <input data-testid="create-password-input" required minLength={6} placeholder="Set a password for them (min. 6 characters)" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className={inputCls} />
              <select data-testid="create-role-select" value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} className={inputCls}>
                <option value="client">Client — gets ZM-XXXX</option>
                <option value="employee">Employee — gets ZE-XXXX</option>
                <option value="admin">Admin — gets ZM-XXXX</option>
              </select>
              <button data-testid="create-user-submit-btn" disabled={creating} className="w-full bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50">
                {creating ? "Provisioning..." : "Provision Account"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
